/**
 * Capturing a spoken utterance in the one format the socket bills against.
 *
 * The wire contract is raw little-endian 16-bit mono PCM at 16 kHz, and it
 * is fixed rather than negotiated for a reason that lives on the server:
 * `voice_turn.pcm_seconds()` derives the billable duration by dividing the
 * byte count by a constant. That arithmetic is only correct if the bytes
 * really are 16 kHz mono s16le. Hand it a compressed container and the
 * duration is a guess; hand it 48 kHz and the visitor is billed triple for
 * audio the recognizer will also mishear.
 *
 * So the sample rate is not a preference here, it is the unit of account,
 * and this module's job is to guarantee it rather than assume it. We ask
 * for a 16 kHz `AudioContext` — browsers will resample the device for us —
 * and then check what we actually got, resampling ourselves if the browser
 * declined the hint. Believing the hint would be the whole bug.
 *
 * `ScriptProcessorNode` is deprecated in favour of `AudioWorklet`, and is
 * used anyway: a worklet needs a separately-fetched module URL, which is a
 * build-and-deploy concern for a node whose entire body is "copy this
 * buffer". The deprecation has no removal date and every current engine
 * ships it.
 */

import { rmsOf } from '@/voice/silenceMonitor';

/** The socket's audio contract. Mirrors `stage_ws/voice_turn.py`. */
export const TARGET_SAMPLE_RATE = 16_000;
export const BYTES_PER_SAMPLE = 2;

/**
 * Hard ceiling on one utterance, mirroring `MAX_UTTERANCE_SECONDS` server
 * side. Enforced here as well as there because the server's copy refuses
 * the turn *after* the bytes have crossed the wire, and the point of the
 * cap is that an open mic never becomes a bill in the first place.
 */
export const MAX_UTTERANCE_SECONDS = 30;
export const MAX_UTTERANCE_BYTES =
  MAX_UTTERANCE_SECONDS * TARGET_SAMPLE_RATE * BYTES_PER_SAMPLE;

/**
 * Below this RMS the capture path is dead, not quiet — the same threshold
 * and the same reasoning as `silenceMonitor.ts`. This is a dead-air guard,
 * deliberately NOT voice-activity detection: a threshold high enough to
 * mean "nobody spoke" is also high enough to swallow someone speaking
 * quietly, and dropping a real utterance is worse than billing a short
 * one. Room tone clears this bar; a muted device does not.
 */
const DEAD_AIR_RMS = 0.0005;

const BUFFER_SIZE = 4096;

/** Clamp to [-1, 1] and scale to signed 16-bit. */
export function floatToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i] ?? 0));
    // Asymmetric on purpose: int16 runs -32768..32767, so the negative
    // and positive full scales have different magnitudes.
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/**
 * Linear-interpolation resample to 16 kHz.
 *
 * Linear rather than a windowed-sinc: the destination is a speech
 * recognizer over a 8 kHz band, and this path only runs at all when a
 * browser has refused the 16 kHz context hint. Correct rate with modest
 * filtering beats a correct filter at the wrong rate.
 */
export function resampleTo16k(input: Float32Array, inputRate: number): Float32Array {
  if (inputRate === TARGET_SAMPLE_RATE || input.length === 0) return input;
  const ratio = inputRate / TARGET_SAMPLE_RATE;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    const pos = i * ratio;
    const lo = Math.floor(pos);
    const hi = Math.min(lo + 1, input.length - 1);
    const frac = pos - lo;
    out[i] = (input[lo] ?? 0) * (1 - frac) + (input[hi] ?? 0) * frac;
  }
  return out;
}

/** Join captured chunks into one buffer. */
export function concatFloat32(chunks: readonly Float32Array[]): Float32Array {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Float32Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

/**
 * Base64-encode samples as little-endian bytes.
 *
 * The endianness is written explicitly through a `DataView` rather than
 * inherited from `Int16Array`'s platform order. Every platform we ship to
 * is little-endian, so the two agree today and the bug would be silent
 * and total on the day one is not — the server would read every sample
 * byte-swapped and transcribe noise.
 *
 * Encoded in chunks because `String.fromCharCode(...bytes)` on a 30-second
 * utterance is a million arguments in one call, which overflows the stack.
 */
export function pcmToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.length * BYTES_PER_SAMPLE);
  const view = new DataView(bytes.buffer);
  for (let i = 0; i < pcm.length; i += 1) {
    view.setInt16(i * BYTES_PER_SAMPLE, pcm[i] ?? 0, true);
  }
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** True when the clip is at digital silence for its whole length. */
export function isDeadAir(samples: Float32Array): boolean {
  return rmsOf(samples) < DEAD_AIR_RMS;
}

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

function resolveAudioContext(): AudioContextCtor | null {
  const g = globalThis as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

export interface PcmRecorderOptions {
  /** Fired when the 30-second ceiling is hit, so the UI can auto-send. */
  readonly onCap?: () => void;
  /** Injectable for tests; defaults to the platform `AudioContext`. */
  readonly audioContextCtor?: AudioContextCtor | null;
}

/**
 * Records one utterance from a live `MediaStream`.
 *
 * One instance per turn is not required — `start`/`stop` may be reused —
 * but the buffer is cleared on each `start`, so a turn never inherits the
 * previous turn's audio and its bill.
 */
export class PcmRecorder {
  private readonly opts: PcmRecorderOptions;
  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private chunks: Float32Array[] = [];
  private frames = 0;
  private recording = false;
  private capped = false;

  constructor(opts: PcmRecorderOptions = {}) {
    this.opts = opts;
  }

  get isRecording(): boolean {
    return this.recording;
  }

  /** True when the last capture stopped because it hit the ceiling. */
  get hitCap(): boolean {
    return this.capped;
  }

  /** Begin capture. Returns false when this browser has no Web Audio. */
  start(stream: MediaStream): boolean {
    const Ctor = this.opts.audioContextCtor ?? resolveAudioContext();
    if (Ctor === null) return false;
    this.teardown();
    this.chunks = [];
    this.frames = 0;
    this.capped = false;

    const ctx = new Ctor({ sampleRate: TARGET_SAMPLE_RATE });
    const source = ctx.createMediaStreamSource(stream);
    const processor = ctx.createScriptProcessor(BUFFER_SIZE, 1, 1);
    // Frames counted at the context's real rate, which is what the ceiling
    // is about — wall-clock seconds of held-open microphone.
    const maxFrames = MAX_UTTERANCE_SECONDS * ctx.sampleRate;

    processor.onaudioprocess = (ev: AudioProcessingEvent): void => {
      if (!this.recording) return;
      // `getChannelData` hands back a buffer it reuses next callback.
      this.chunks.push(ev.inputBuffer.getChannelData(0).slice());
      this.frames += ev.inputBuffer.length;
      if (this.frames >= maxFrames && !this.capped) {
        this.capped = true;
        this.opts.onCap?.();
      }
    };

    source.connect(processor);
    // A ScriptProcessorNode only pulls while it is part of a live graph.
    // Routing it to the destination would echo the visitor back at
    // themselves, so it terminates in a muted gain node instead.
    const sink = ctx.createGain();
    sink.gain.value = 0;
    processor.connect(sink);
    sink.connect(ctx.destination);

    this.ctx = ctx;
    this.source = source;
    this.processor = processor;
    this.recording = true;
    return true;
  }

  /** End capture and return the utterance in the socket's format. */
  stop(): Int16Array {
    if (!this.recording) return new Int16Array(0);
    this.recording = false;
    const rate = this.ctx?.sampleRate ?? TARGET_SAMPLE_RATE;
    const joined = concatFloat32(this.chunks);
    this.chunks = [];
    this.teardown();
    if (isDeadAir(joined)) return new Int16Array(0);
    const resampled = resampleTo16k(joined, rate);
    const trimmed =
      resampled.length * BYTES_PER_SAMPLE > MAX_UTTERANCE_BYTES
        ? resampled.subarray(0, MAX_UTTERANCE_BYTES / BYTES_PER_SAMPLE)
        : resampled;
    return floatToPcm16(trimmed);
  }

  /** Release the audio graph without producing a turn. */
  cancel(): void {
    this.recording = false;
    this.chunks = [];
    this.teardown();
  }

  private teardown(): void {
    if (this.processor !== null) {
      this.processor.onaudioprocess = null;
      this.processor.disconnect();
      this.processor = null;
    }
    this.source?.disconnect();
    this.source = null;
    if (this.ctx !== null) {
      void this.ctx.close();
      this.ctx = null;
    }
  }
}
